/**
 * Feeds API - Search (Posts MVP)
 *
 * Contract for POST /v1/feeds/search — server-backed full-text search over
 * the user's accessible posts (main feed + every subgroup feed they belong
 * to). School scoping and subgroup-membership resolution happen server-side;
 * the client never supplies a trusted schoolId.
 *
 * Mirrored by the backend schema at
 * `apps/backend/src/api/feeds/v1/use-cases/(search)/search/schema.ts`.
 */

import { z } from 'zod'

/**
 * Content types searchable by feed search. Matches the backend Prisma
 * `ContentType` enum exactly (which is wider than the 4-value entity
 * `ContentTypeSchema`) so the post-type filter chip can target every kind.
 */
export const FeedSearchContentTypeSchema = z.enum(['POST', 'ANNOUNCEMENT', 'NEWS', 'ALERT', 'EVENT', 'ACTION_ITEM'])
export type FeedSearchContentType = z.infer<typeof FeedSearchContentTypeSchema>

// ============================================================================
// Request
// ============================================================================

export const FeedSearchFiltersSchema = z
  .object({
    /**
     * Client-supplied UserGroup.publicId set for the group filter chip. NEVER
     * trusted directly — the server intersects this with the user's
     * server-resolved authorized groups. (Audience is group-based; content is
     * targeted at groups, not sections.)
     */
    groupIds: z.array(z.string()).optional(),
    contentTypes: z.array(FeedSearchContentTypeSchema).optional(),
    publishedAfter: z.iso.datetime().optional(),
    publishedBefore: z.iso.datetime().optional()
  })
  // REQUEST schema: strict so a typo'd filter key (e.g. `filterTypes`) is a 400
  // rather than a silent empty result. passthrough is reserved for RESPONSE
  // schemas (forward-compat with server-added fields).
  .strict()

export type FeedSearchFilters = z.infer<typeof FeedSearchFiltersSchema>

export const FeedSearchRequestSchema = z.object({
  q: z.string().min(1).max(200),
  limit: z.number().int().min(1).max(50).optional().prefault(20),
  offset: z.number().int().min(0).optional().prefault(0),
  filters: FeedSearchFiltersSchema.optional()
})

export type FeedSearchRequest = z.infer<typeof FeedSearchRequestSchema>

// ============================================================================
// Response
// ============================================================================

/**
 * A single matched post, shaped to render through the mobile `FeedItem`
 * component without a follow-up fetch. `passthrough` so future server-added
 * fields don't break older clients.
 */
export const FeedSearchResultItemSchema = z
  .object({
    id: z.string(),
    contentRecordId: z.string(),
    streamActivityId: z.string().nullable(),
    /**
     * Best-effort Section.publicId for the post. Audience is group-based, so
     * this is frequently null and is NOT load-bearing for navigation (use
     * streamActivityId).
     */
    sectionId: z.string().nullable(),
    schoolId: z.string(),
    contentType: FeedSearchContentTypeSchema,
    contentTitle: z.string().nullable(),
    description: z.string().nullable(),
    publishedAt: z.string().nullable(),
    relevanceScore: z.number().optional()
  })
  .passthrough()

export type FeedSearchResultItem = z.infer<typeof FeedSearchResultItemSchema>

export const FeedSearchResponseMetaSchema = z
  .object({
    /**
     * True when at least one more result exists beyond this page. Replaces a
     * `total` count (which required a second full FULLTEXT+JOIN+GROUP BY pass);
     * the server derives it from a LIMIT+1 probe.
     */
    hasMore: z.boolean(),
    limit: z.number().int(),
    offset: z.number().int(),
    searchTime: z.number().optional()
  })
  .passthrough()

export type FeedSearchResponseMeta = z.infer<typeof FeedSearchResponseMetaSchema>

export const FeedSearchResponseSchema = z
  .object({
    data: z.array(FeedSearchResultItemSchema),
    meta: FeedSearchResponseMetaSchema,
    message: z.string()
  })
  // RESPONSE schema: passthrough so a future server-added top-level field
  // (e.g. facets) does not break older clients' Zod parse.
  .passthrough()

export type FeedSearchResponse = z.infer<typeof FeedSearchResponseSchema>
